import React, { useEffect } from 'react';
import { NextSeo } from 'next-seo';
import type { GetServerSideProps } from 'next/types';

import { useTranslation } from '@crehana/i18n';

import {
  initializeApollo,
  getG11nAndTranslationsFromServer,
} from '@crehana/next';

import { getSourceEventParams, useAmplitude } from '@crehana/analytics';

import { B2BLandingWrapper } from '@/views/B2BLanding/B2BLanding.styles';
import B2BLandingDefaultSeo from '@/views/B2BLanding/B2BLandingDefaultSeo';
import { Header, Footer } from '@/views/B2BLanding/components';
import { FreeTrialBanner } from '@/views/B2BLanding/sections';
import {
  BannerSection,
  AboutSection,
  FeaturesSection,
  CoursesSection,
  ProfessorsSection,
  InfoSection,
} from '@/views/B2BLanding/pages/TrackDetail/sections';
import { usePageView } from '@/shared/hooks';

import usePersistQueryParams from '@/shared/hooks/usePersistQueryParams';

import localeViewEs from '../../i18n/localeEs';

import localeViewPt from '../../i18n/localePt';

import { AMPLITUDE_TRACKS } from '../../events';

import { TrackProps } from './data/types';
import localeEs from './i18n/localeEs';
import localePt from './i18n/localePt';

export interface PageProps {
  track: TrackProps;
}

const PAGE_KEY = 'b2b-landing-track-detail';

const TrackDetailPage: React.FC<React.PropsWithChildren<PageProps>> = ({
  track,
}) => {
  usePersistQueryParams();
  const { addResourceBundle } = useTranslation();
  const { t } = useTranslation();
  const { logEvent } = useAmplitude();

  addResourceBundle(
    {
      es: { [PAGE_KEY]: { ...localeEs.es, ...localeViewEs.es } },
      pt: { [PAGE_KEY]: { ...localePt.pt, ...localeViewPt.pt } },
    },
    { injectOnce: true },
  );

  usePageView({ type: 'page' });

  useEffect(() => {
    logEvent({
      event: AMPLITUDE_TRACKS.LANDING_PAGE_VIEWED,
      data: {
        ...getSourceEventParams(),
        Product: `B2B Track ${track.name}`,
        pageSlug: track.slug,
        LandingName: `Track ${track.name}`,
      },
    });
  }, [logEvent]);

  return (
    <B2BLandingWrapper>
      <B2BLandingDefaultSeo />
      <NextSeo
        title={t(track.name)}
        description={t(track.subTitle)}
        canonical={`/business/tracks/${track.slug}/`}
        openGraph={{
          title: `${t(track.name)} | ${t('CREHANA_FOR_BUSINESS')}`,
          description: t(track.subTitle),
        }}
      />
      <Header position="sticky" />
      <BannerSection title={t(track.title)} description={t(track.subTitle)} />
      <AboutSection {...track.about} />
      <FeaturesSection features={track.features} />
      <CoursesSection slug={track.slug} />
      <ProfessorsSection professors={track.professors} />
      <InfoSection {...track.info} />
      <FreeTrialBanner />
      <Footer />
    </B2BLandingWrapper>
  );
};

export const getServerSideProps: GetServerSideProps = async ({
  query,
  req,
}) => {
  const slug = Array.isArray(query.slug) ? query.slug[0] : query.slug;
  const track = (await import(`./data/${slug}`)).default;

  const apolloClient = initializeApollo();
  const g11nData = await getG11nAndTranslationsFromServer({
    headers: req.headers,
    pageKey: PAGE_KEY,
    defaultTranslations: {
      es: { [PAGE_KEY]: { ...localeEs.es, ...localeViewEs.es } },
      pt: { [PAGE_KEY]: { ...localePt.pt, ...localeViewPt.pt } },
    },
  });

  return {
    props: {
      initialApolloState: apolloClient.cache.extract(),
      slug,
      track,
      ...g11nData,
    },
  };
};
export default TrackDetailPage;
